// Payer rules the page reads (APPEALS_HANDOFF §11, D129/D131). Per payer AND plan,
// from Settings › Payers, never typed per claim. "Unknown"/"not checked" are
// first-class values. Fax numbers are 555 numbers in the mock.

/** The eight ways an appeal can go out, in the fixed Settings select order (§15). */
export type Channel =
  | 'api'
  | 'website'
  | 'secure_message'
  | 'fax'
  | 'certified_mail'
  | 'certified_mail_plain'
  | 'first_class'
  | 'secure_email';

export const CHANNELS_IN_ORDER: { id: Channel; label: string }[] = [
  { id: 'api', label: 'Payer’s API' },
  { id: 'website', label: 'Payer’s website' },
  { id: 'secure_message', label: 'Payer’s secure message' },
  { id: 'fax', label: 'Fax' },
  { id: 'certified_mail', label: 'Certified mail with return receipt' },
  { id: 'certified_mail_plain', label: 'Certified mail' },
  { id: 'first_class', label: 'First-class mail' },
  { id: 'secure_email', label: 'Secure email' },
];

/** Which "destination box" verb a channel uses (§5 item 7). */
export function destLabel(ch: Channel): string {
  switch (ch) {
    case 'api':
    case 'website':
      return 'Files it on';
    case 'secure_message':
      return 'Sends it through';
    case 'fax':
      return 'Faxes it to';
    case 'secure_email':
      return 'Emails it securely to';
    default:
      return 'Mail to';
  }
}

/** The label on the primary Send button (§8). */
export function sendButtonLabel(ch: Channel, payerName: string, destName: string): string {
  switch (ch) {
    case 'api':
    case 'website':
      return `Send on ${destName}`;
    case 'secure_message':
      return `Send through ${destName}`;
    case 'fax':
      return 'Send by fax';
    case 'secure_email':
      return 'Send by secure email';
    case 'first_class':
      return 'Send by mail';
    default:
      return 'Send by certified mail';
  }
}

export function isMail(ch: Channel): boolean {
  return ch === 'certified_mail' || ch === 'certified_mail_plain' || ch === 'first_class';
}

export interface PayerPlanRule {
  /** Key, e.g. "Aetna PPO", "Guardian DHMO", "Cigna". */
  key: string;
  payer: string; // short, e.g. "Aetna"
  plan: string; // e.g. "PPO", "DHMO", "Dental"
  accepts: Channel[];
  /** First-choice order ELVA uses. */
  order: Channel[];
  /** The payer's own name for its first channel, e.g. "Aetna’s dental website". */
  firstDestName: string;
  /** The detail line under the destination name (e.g. "Dispute a claim, in the EOB tool"). */
  firstDestDetail?: string;
  /** For a mail channel, the mailing address (or null = use the denial letter). */
  address: string | null;
  /** Fax number for the plan (or null). */
  faxNumber?: string | null;
  /** A step the payer requires before an appeal (Delta). */
  firstStep?: string | null;
  /** Locked channel the payer requires (Bluebonnet). */
  required?: boolean;
  /** Source + checked date for the destination box. */
  source: string;
  checkedOn: string;
  deadlineBasis: string; // "180 days from the decision"
  /**
   * False when the payer's appeal window isn't researched (D241/D256). Default: researched
   * when `source` starts with "From". Unconfirmed windows show "Not confirmed · ELVA aims to
   * send within 30 days" and are never shown as the payer's deadline.
   */
  windowConfirmed?: boolean;
  answerWithin: string | null; // "60 days" | "45 working days" | null
  /** Short "what the payer accepts" facts for Settings chips. */
  acceptsWords: string;
}

export const PAYER_PLAN_RULES: PayerPlanRule[] = [
  {
    key: 'Aetna PPO',
    payer: 'Aetna',
    plan: 'PPO',
    accepts: ['website', 'fax', 'certified_mail'],
    order: ['website', 'fax', 'certified_mail'],
    firstDestName: 'Aetna’s dental website',
    firstDestDetail: 'Dispute a claim, in the EOB tool',
    address: 'Aetna Dental Appeals Team, PO Box 14957, Lexington, KY 40512-4957',
    faxNumber: '(555) 010-4400',
    source: 'From Aetna’s dispute page',
    checkedOn: 'Oct 2',
    deadlineBasis: '180 days from the decision',
    answerWithin: '60 days',
    acceptsWords: 'Website (EOB tool), fax, mail',
  },
  {
    key: 'Guardian PPO',
    payer: 'Guardian',
    plan: 'PPO',
    accepts: ['secure_message', 'fax', 'certified_mail'],
    order: ['secure_message', 'fax', 'certified_mail'],
    firstDestName: 'Guardian Secure Channel',
    address: 'Guardian, Group Dental Claims, PO Box 981572, El Paso, TX 79998',
    faxNumber: '(555) 010-2201',
    source: 'From Guardian’s provider portal',
    checkedOn: 'Oct 3',
    deadlineBasis: '180 days from its payment',
    // Demo: the one queue claim (Karen Liu, CLM-0917) whose payer window isn't confirmed.
    windowConfirmed: false,
    answerWithin: null,
    acceptsWords: 'Secure Channel, fax (PPO), mail',
  },
  {
    key: 'Guardian DHMO',
    payer: 'Guardian',
    plan: 'DHMO',
    accepts: ['secure_message', 'fax', 'certified_mail'],
    order: ['secure_message', 'fax', 'certified_mail'],
    firstDestName: 'Guardian Secure Channel',
    address: 'Guardian, Group Dental Claims, PO Box 981572, El Paso, TX 79998',
    faxNumber: '(555) 010-2202',
    source: 'From Guardian’s provider portal',
    checkedOn: 'Oct 3',
    deadlineBasis: '180 days from its payment',
    answerWithin: null,
    acceptsWords: 'Secure Channel, fax (DHMO), mail',
  },
  {
    key: 'Delta Dental PPO',
    payer: 'Delta',
    plan: 'PPO',
    accepts: ['website'],
    order: ['website'],
    firstDestName: 'Delta’s Provider Tools',
    firstDestDetail: 'Dispute a claim',
    address: null,
    firstStep: 'Claim adjustment, then dispute',
    source: 'From Delta’s Provider Tools',
    checkedOn: 'Oct 2',
    deadlineBasis: '180 days from the decision',
    answerWithin: '45 working days',
    acceptsWords: 'Website (Provider Tools)',
  },
  {
    key: 'Cigna',
    payer: 'Cigna',
    plan: 'Dental',
    accepts: ['certified_mail'],
    order: ['certified_mail'],
    firstDestName: 'Cigna Dental',
    firstDestDetail: 'the appeals address on Cigna’s denial letter of Oct 7',
    address: null, // unknown → use the denial letter
    source: 'From Cigna’s denial letter of Oct 7',
    checkedOn: 'Oct 7',
    deadlineBasis: '180 days from the payment or denial',
    answerWithin: '60 days',
    acceptsWords: 'Mail · address not published',
  },
  {
    key: 'Humana Dental',
    payer: 'Humana',
    plan: 'Dental',
    accepts: ['certified_mail', 'secure_email'],
    order: ['certified_mail', 'secure_email'],
    firstDestName: 'Humana',
    firstDestDetail: 'the address on the member’s ID card',
    address: 'the address on the member’s ID card',
    source: 'From Humana’s provider manual',
    checkedOn: 'Oct 1',
    deadlineBasis: '180 days',
    answerWithin: '30 days',
    acceptsWords: 'Mail; secure email for reconsiderations',
  },
  {
    key: 'MetLife',
    payer: 'MetLife',
    plan: 'Dental',
    accepts: ['certified_mail'],
    order: ['certified_mail'],
    firstDestName: 'MetLife',
    firstDestDetail: 'the appeals address on the denial letter',
    address: null, // not checked
    source: 'Not checked — uses the denial letter address',
    checkedOn: '—',
    deadlineBasis: '180 days',
    windowConfirmed: false,
    answerWithin: null,
    acceptsWords: 'Mail · not checked yet',
  },
  {
    key: 'Bluebonnet Dental Plan',
    payer: 'Bluebonnet',
    plan: 'Dental',
    accepts: ['certified_mail'],
    order: ['certified_mail'],
    firstDestName: 'Bluebonnet Dental Plan',
    address: 'Bluebonnet Dental Plan, Appeals, PO Box 5520, San Antonio, TX 78201',
    required: true,
    source: 'From Bluebonnet’s provider agreement',
    checkedOn: 'Sep 20',
    deadlineBasis: '180 days',
    answerWithin: null,
    acceptsWords: 'Certified mail only (required)',
  },
  {
    key: 'UnitedHealthcare Dental',
    payer: 'UnitedHealthcare',
    plan: 'Dental',
    accepts: ['website', 'fax', 'certified_mail'],
    order: ['website', 'fax', 'certified_mail'],
    firstDestName: 'UnitedHealthcare’s provider website',
    address: 'UnitedHealthcare Dental Appeals, PO Box 30567, Salt Lake City, UT 84130',
    faxNumber: '(555) 010-8800',
    source: 'From UHC’s provider manual',
    checkedOn: 'Sep 28',
    deadlineBasis: '180 days',
    answerWithin: null,
    acceptsWords: 'Website, fax, mail (some plans)',
  },
];

/** The fallback rule if nothing matches — Aetna PPO is first in the list. */
function firstRule(): PayerPlanRule {
  const r = PAYER_PLAN_RULES[0];
  if (!r) throw new Error('PAYER_PLAN_RULES is empty');
  return r;
}

/** Find the rule for a claim's payer.name (e.g. "Aetna PPO", "Cigna DPPO", "Delta Dental PPO", "Guardian"). */
export function ruleForPayerName(payerName: string): PayerPlanRule {
  const norm = payerName.toLowerCase();
  // exact key first
  const exact = PAYER_PLAN_RULES.find((r) => r.key.toLowerCase() === norm);
  if (exact) return exact;
  // by short payer name, preferring PPO
  const short = norm.split(' ')[0] ?? norm;
  const byPayer = PAYER_PLAN_RULES.filter((r) => r.payer.toLowerCase() === short || norm.startsWith(r.payer.toLowerCase()));
  if (byPayer.length > 0) {
    const ppo = byPayer.find((r) => r.plan === 'PPO');
    if (ppo) return ppo;
    const first = byPayer[0];
    if (first) return first;
  }
  // Cigna DPPO etc.
  const loose = PAYER_PLAN_RULES.find((r) => norm.startsWith(r.payer.toLowerCase()));
  return loose ?? firstRule();
}
